import { Controller, useForm } from "react-hook-form";
import { useCallback, useEffect } from "react";
import { useParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";

import { acuityKeys } from "@/features/acuity/api/keyFactory";
import type { AcuityScore, UUID } from "@/core/types";
import { Button2 } from "@/ui2/atoms/Button";
import { ModalControlled2, ModalDivider } from "@/ui2/molecules/Modal";
import { showToast } from "@/providers/ToastProvider";
import { TextField2 } from "@/ui2/molecules/TextField";

import { usePostAcuityScore } from "../api/mutations/usePostAcuityScore";

import { AcuityScoreSelect } from "./AcuityScoreSelect";
import { AcuityTransition } from "./AcuityTransition";
import { ApprovalAlert } from "./ApprovalAlert";
import { ReasoningSelect } from "./ReasoningSelect";

type ReportAcuityModalProps = {
  isModalOpen: boolean;
  setIsModalOpen: (isOpen: boolean) => void;
};

type AcuityFormValues = {
  additional_details: string;
  reasoning: string;
  score: AcuityScore | null;
};

const DEFAULT_VALUES: AcuityFormValues = { additional_details: "", reasoning: "", score: null };
const MAX_CHAR_LENGTH = 1_000;

export function ReportAcuityModal({ isModalOpen, setIsModalOpen }: ReportAcuityModalProps) {
  const { patientId } = useParams<{ patientId: UUID }>();
  const queryClient = useQueryClient();

  const { control, formState, handleSubmit, reset } = useForm<AcuityFormValues>({
    defaultValues: DEFAULT_VALUES,
    mode: "onSubmit",
  });

  useEffect(() => {
    if (!isModalOpen) reset(DEFAULT_VALUES);
  }, [isModalOpen, reset]);

  const { isPending, mutate } = usePostAcuityScore({
    meta: {
      component: "ReportAcuityModal",
      patientId,
    },
    onError: () => {
      showToast([
        {
          heading: "Something went wrong. Please try submitting again.",
          variant: "error",
        },
      ]);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: acuityKeys.current(patientId) });
      setIsModalOpen(false);

      showToast([
        {
          heading: "Acuity change request submitted.",
          text: "Pending clinical approval.",
          variant: "success",
        },
      ]);
    },
  });

  const onSubmit = useCallback(
    (formValues: AcuityFormValues) =>
      mutate({ patientId, ...formValues, score: formValues.score! }),
    [mutate, patientId],
  );

  const isCancelButtonDisabled = isPending || formState.isSubmitting;

  const isSubmitButtonDisabled = isPending || formState.isSubmitting || !formState.isValid;

  return (
    <ModalControlled2
      className="tw:min-w-150"
      isOpen={isModalOpen}
      onOpenChange={setIsModalOpen}
      title="Report Acuity"
    >
      <form className="tw:flex tw:flex-col tw:gap-6" onSubmit={handleSubmit(onSubmit)}>
        <AcuityTransition>
          {(acuityScore) => (
            <Controller
              control={control}
              name="score"
              render={({ field }) => (
                <AcuityScoreSelect
                  acuityScore={acuityScore}
                  isRequired
                  onChange={field.onChange}
                  value={field.value}
                />
              )}
              rules={{ required: true }}
            />
          )}
        </AcuityTransition>

        <Controller
          control={control}
          name="reasoning"
          render={({ field }) => (
            <ReasoningSelect
              isRequired
              onChange={field.onChange}
              placeholder="Select reasoning for this change"
              value={field.value}
            />
          )}
          rules={{ required: true }}
        />

        <Controller
          control={control}
          name="additional_details"
          render={({ field }) => (
            <TextField2
              label="Additional Details"
              maxLength={MAX_CHAR_LENGTH}
              mode="textarea"
              onChange={field.onChange}
              placeholder="Please provide additional details"
              textAreaProps={{ className: "tw:min-h-19.5 tw:max-h-40" }}
              value={field.value}
            />
          )}
          rules={{
            maxLength: MAX_CHAR_LENGTH,
          }}
        />

        <ApprovalAlert />

        <ModalDivider />

        <div className="tw:flex tw:justify-end tw:gap-x-3">
          <Button2
            isDisabled={isCancelButtonDisabled}
            onPress={() => setIsModalOpen(false)}
            size="md"
            variant="secondary"
          >
            Cancel
          </Button2>

          <Button2
            isDisabled={isSubmitButtonDisabled}
            isLoading={isPending}
            size="md"
            type="submit"
            variant="primary"
          >
            Submit For Approval
          </Button2>
        </div>
      </form>
    </ModalControlled2>
  );
}
